import {
  Brand,
  NormalizedEntity,
  CardTypes,
  TemplateSize
} from "@fresh8/types/src";
import { BetData, Market, SportEvent } from "@fresh8/types/src/betData";
import { Preview } from "@fresh8/types/src/preview";
import {
  FTR_GROUP,
  INSIGHT_GROUP,
  LIST_GROUP,
  MAIN_MARKET_GROUP,
  PROMO_GROUP,
  WDW_GROUP,
  WW_GROUP
} from "../../constants/market-types";

import { Sport } from "@fresh8/types/src/language";
import { computed, ComputedRef } from "vue";

export type CreationContext = {
  betData: BetData;
  brand: Brand;
  size: TemplateSize;
  preview: Preview;
} & toRaw<AdditionalFeatures>;

type toRaw<T extends Record<string, ComputedRef<any> | undefined>> = {
  [Key in keyof T]: GetComputedValueType<T[Key]>;
};

type GetComputedValueType<
  T extends ComputedRef<any> | undefined
> = T extends ComputedRef<infer Inner> ? Inner : undefined;

export interface AdditionalFeatures {
  displayOfferCard?: ComputedRef<boolean>;
  insightABTestingEnabled?: ComputedRef<boolean>;
  [key: string]: ComputedRef<any> | undefined;
}

export const additionalFeatureDefaults = {
  displayOfferCard: computed(() => false),
  insightABTestingEnabled: computed(() => false)
};

export type CardCreationFunction = (
  event: EventGroup,
  context: CreationContext
) => Array<MarketCard>;

export type Markets = NormalizedEntity<Market>;

export interface EventMarketGroups {
  [PROMO_GROUP]?: string[];
  [FTR_GROUP]?: string[];
  [LIST_GROUP]?: string[];
  [MAIN_MARKET_GROUP]?: string[];
  [INSIGHT_GROUP]?: string[];
  [WDW_GROUP]?: string[];
  [WW_GROUP]?: string[];
}

/**
 * DATA CLASS
 *
 * data class for cards
 */

export class MarketCard {
  public type: CardTypes;
  public eventId: string;
  public marketId: string;
  public marketIds: string[];
  public optionIds: string[] | null;

  constructor(options: {
    type: CardTypes;
    eventId: string;
    marketIds: string[];
    optionIds: string[] | null;
  }) {
    this.type = options.type;
    this.eventId = options.eventId;
    this.marketId = options.marketIds[0];
    this.marketIds = options.marketIds;
    this.optionIds = options.optionIds;
  }
}

/**
 * DATA CLASS
 *
 * data class with helper methods for grouping cards for events
 */

export class EventGroup {
  public id: string;
  public isPriority: boolean;
  public date: Date;
  public markets: EventMarketGroups;
  public sport: Sport;
  public data: SportEvent;
  private cards: Array<MarketCard>;

  constructor(event: SportEvent, markets: EventMarketGroups) {
    this.id = event.id;
    this.data = event;
    this.isPriority = event.priorityEvent || false;
    this.date = new Date(event.fixture);
    this.markets = markets;
    this.sport = event.sport;
    this.cards = [];
  }

  has(cardType: CardTypes) {
    return this.cards.some(t => t.type === cardType);
  }

  addCard(card: MarketCard | Array<MarketCard>) {
    if (Array.isArray(card)) {
      this.cards = this.cards.concat(...card);
      return this;
    }

    this.cards.push(card);

    return this;
  }

  getCards() {
    return this.cards;
  }
}
